import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, ChevronDown, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { buildEventPath, matchesSearch } from '../utils/eventUtils';

const SUGGESTION_LIMIT = 5;
const DEBOUNCE_MS = 300;

// ─── Dropdown de sugestões ─────────────────────────────────────────────────

function SuggestionsDropdown({ results, onPick }) {
  return (
    <div
      className={cn(
        'absolute left-0 right-0 top-[calc(100%+8px)] z-50',
        'bg-popover text-popover-foreground rounded-2xl border shadow-lg overflow-hidden',
        'py-2'
      )}
    >
      {results.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">
          Nenhum evento encontrado.
        </p>
      ) : (
        results.map((event) => (
          <Link
            key={event.id}
            to={buildEventPath(event.id, event.title)}
            onClick={() => onPick()}
            className="flex items-center gap-3 px-3 py-2 mx-1 rounded-xl hover:bg-muted transition-colors"
          >
            <div className="w-11 h-11 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              {event.imageUrl && (
                <img
                  src={event.imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{event.title}</p>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{event.dateLabel}</span>
                {event.city && (
                  <>
                    <span>·</span>
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{event.city}</span>
                  </>
                )}
              </div>
            </div>
          </Link>
        ))
      )}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────

export function PublicHeader({ events = [], onSearch }) {
  const [scrolled, setScrolled] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Versão compacta ao rolar só em md+, onde o header é sticky. No celular
  // ele rola junto com a página: encolher ali mudaria a altura de um
  // elemento no fluxo e a página "pularia" ao passar dos 30px.
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const update = () => setScrolled(desktop.matches && window.scrollY > 30);
    update();
    window.addEventListener('scroll', update, { passive: true });
    desktop.addEventListener('change', update);
    return () => {
      window.removeEventListener('scroll', update);
      desktop.removeEventListener('change', update);
    };
  }, []);

  // Debounce ~300ms só pro dropdown de sugestões — o filtro da lista
  // "Próximos Eventos" (Home) só reage a um commit explícito (Enter, ou
  // campo esvaziado), não a cada tecla.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Fecha o dropdown ao clicar fora — em vez de onBlur, que fecharia antes
  // do clique numa sugestão registrar (blur dispara antes do click).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isOpen]);

  const suggestions = debouncedQuery.trim()
    ? events.filter((e) => matchesSearch(e, debouncedQuery)).slice(0, SUGGESTION_LIMIT)
    : [];

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.trim().length > 0);
    // Campo esvaziado: volta a mostrar todos os eventos (respeitando só a
    // categoria) sem precisar de Enter.
    if (!value.trim()) onSearch?.('');
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    onSearch?.(query);
    setIsOpen(false);
    e.currentTarget.blur();
  };

  return (
    // Posicionador transparente — cria as margens do header flutuante. Fixo
    // no topo só em md+; no celular rola junto com a página (atrapalhava a
    // leitura). relative mantém o z-50 valendo pro dropdown de sugestões.
    <header className="relative md:sticky md:top-0 z-50 pt-3 pb-1 px-4 md:px-6 bg-transparent">

      {/* Card flutuante — vidro fosco, discreto, premium */}
      <div
        className={cn(
          'mx-auto transition-all duration-300',
          'bg-white/[0.15] backdrop-blur-xl rounded-2xl',
          'border border-black/[0.06]',
          'shadow-[0_1px_16px_rgba(0,0,0,0.05)]',
          scrolled
            ? 'max-w-4xl px-4 md:px-5 py-2'
            : 'max-w-5xl px-5 md:px-6 py-3'
        )}
      >
        <div className="flex items-center gap-3 md:gap-5">

          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/icon.png"
              alt="Joyn"
              className={cn(
                'w-auto object-contain transition-all duration-300',
                scrolled ? 'h-9' : 'h-11'
              )}
            />
          </Link>

          {/* Cidade — estrutura visual, sem lógica ainda */}
          <div
            className={cn(
              'hidden lg:flex items-center flex-shrink-0 transition-all duration-300 overflow-hidden',
              scrolled ? 'max-w-0 opacity-0' : 'max-w-xs opacity-100'
            )}
          >
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors whitespace-nowrap"
            >
              <MapPin className="w-4 h-4 flex-shrink-0" />
              Joinville
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </button>
          </div>

          {/* Busca — dropdown de sugestões enquanto digita (debounce), Enter
              filtra "Próximos Eventos" na Home sem navegar. */}
          <div ref={containerRef} className="relative flex-1 min-w-0 max-w-3xl mx-auto">
            {/* focus-within no pill (não no input) — o destaque de foco
                acompanha o formato arredondado do resto da UI, em vez do
                retângulo reto que o outline nativo do navegador desenha em
                volta só do <input>. */}
            <div className="flex items-center gap-2.5 rounded-full bg-background ring-1 ring-border shadow-sm px-5 py-2.5 min-w-0 transition-shadow focus-within:ring-2 focus-within:ring-orange-500">
              <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input
                type="text"
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsOpen(query.trim().length > 0)}
                placeholder="Descobrir eventos: música, shows, esportes..."
                // focus:outline-none (em vez de só outline-none) tem
                // especificidade maior que a regra global ":focus-visible"
                // de App.css — sem isso, o outline preto padrão do
                // navegador continua ganhando por ordem de carregamento do
                // CSS mesmo com outline-none presente.
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none focus:outline-none"
              />
            </div>

            {isOpen && (
              <SuggestionsDropdown results={suggestions} onPick={() => setIsOpen(false)} />
            )}
          </div>

          <Button asChild size="sm" className="rounded-full px-5 flex-shrink-0">
            <Link to="/login">Entrar</Link>
          </Button>

        </div>
      </div>

    </header>
  );
}
